import { UserRouteActionV1Model } from '@core/models/entities/implementations/user-route-action-v1';
import { UserSiteV1Model } from '@core/models/entities/implementations/user-site-v1';

import {
  OccupationAvailability,
  OccupationClaim,
  OccupationContribution,
  OccupationHolder,
  OccupationLane,
  OccupationSeries,
  OccupationStep
} from './models/occupation.model';
import { canLadder } from './occupation-scales';

/**
 * Flattens one stopover's `infrastructure_usages` into claims.
 *
 * Usages that cannot contribute to a curve are dropped here rather than downstream:
 * an unparseable timestamp, a non-positive quantity, or an interval that ends at or
 * before it starts would each render as a zero-width artefact.
 */
export function collectActionClaims(
  action: UserRouteActionV1Model,
  routeUid: string
): OccupationClaim[] {
  const claims: OccupationClaim[] = [];

  for (const usage of action.infrastructure_usages ?? []) {
    const startMs = Date.parse(usage.start);
    const endMs = Date.parse(usage.end);

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    if (endMs <= startMs) continue;
    if (!(usage.quantity > 0)) continue;

    claims.push({
      siteUid: action.site_uid,
      infrastructureUid: usage.infrastructure_uid,
      routeUid,
      stopoverUid: action.uid,
      quantity: usage.quantity,
      startMs,
      endMs
    });
  }

  return claims;
}

/**
 * Cuts the timeline at every interval edge and counts what covers each slice.
 *
 * Intervals are half-open, so a claim covers a slice only when it spans the slice
 * entirely: `claim.startMs <= slice.fromMs && claim.endMs >= slice.toMs`. A claim ending
 * exactly where the next begins therefore never overlaps it — the property the whole
 * chart's honesty rests on.
 */
export function sweep(claims: readonly OccupationClaim[]): OccupationStep[] {
  if (claims.length === 0) return [];

  const edges = [...new Set(claims.flatMap((c) => [c.startMs, c.endMs]))].sort(
    (a, b) => a - b
  );

  const steps: OccupationStep[] = [];

  for (let i = 0; i < edges.length - 1; i++) {
    const fromMs = edges[i];
    const toMs = edges[i + 1];
    const holders: OccupationHolder[] = [];
    let used = 0;

    for (const c of claims) {
      if (c.startMs <= fromMs && c.endMs >= toMs) {
        used += c.quantity;
        holders.push({
          routeUid: c.routeUid,
          stopoverUid: c.stopoverUid,
          quantity: c.quantity
        });
      }
    }

    steps.push({ fromMs, toMs, used, holders });
  }

  return steps;
}

/**
 * First-fit lane packing for the slot-ladder form: a claim takes the earliest lane whose
 * last occupant has already finished. A claim of quantity n occupies n lanes, because a
 * lane models one unit of the infrastructure.
 */
export function packLanes(claims: readonly OccupationClaim[]): OccupationLane[] {
  const lanes: OccupationLane[] = [];
  const ordered = [...claims].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

  for (const c of ordered) {
    const units = Math.max(1, Math.round(c.quantity));

    for (let unit = 0; unit < units; unit++) {
      const free = lanes.find((lane) => lane[lane.length - 1].endMs <= c.startMs);
      if (free) {
        free.push(c);
      } else {
        lanes.push([c]);
      }
    }
  }

  return lanes;
}

export interface OccupationSummary {
  peak: number;
  busyMs: number;
  overMs: number;
  peakUtilization: number | null;
  meanUtilization: number | null;
}

/**
 * Aggregates a swept series. Utilizations are null — never Infinity or a fabricated 1 —
 * when there is no usable capacity to divide by, so renderers must branch on null.
 */
export function summarize(
  steps: readonly OccupationStep[],
  capacity: number | null
): OccupationSummary {
  let peak = 0;
  let busyMs = 0;
  let overMs = 0;
  let weightedMs = 0;

  for (const s of steps) {
    const duration = s.toMs - s.fromMs;
    peak = Math.max(peak, s.used);
    if (s.used > 0) busyMs += duration;
    if (capacity !== null && s.used > capacity) overMs += duration;
    weightedMs += s.used * duration;
  }

  const spanMs = steps.length ? steps[steps.length - 1].toMs - steps[0].fromMs : 0;
  const usable = capacity !== null && capacity > 0;

  return {
    peak,
    busyMs,
    overMs,
    peakUtilization: usable ? peak / capacity : null,
    meanUtilization: usable && spanMs > 0 ? weightedMs / (capacity * spanMs) : null
  };
}

// Safe: the uid pattern ^[a-z0-9_A-Z-.]+$ cannot contain a pipe.
const KEY_SEPARATOR = '|';

/**
 * Flattens the availabilities every site declares, adding up rows that name the same
 * infrastructure twice. Usages already sum on repeats, and a site that declares its dock
 * bank as two rows of 2 and 3 offers 5 docks; keeping only the last row would report 3
 * and paint a breach that never happened.
 *
 * Sites are deduplicated by uid first, because the same site row reaches this function
 * once per route that visits it.
 */
export function collectAvailabilities(
  sites: readonly UserSiteV1Model[]
): OccupationAvailability[] {
  const totals = new Map<string, OccupationAvailability>();
  const seenSites = new Set<string>();

  for (const site of sites) {
    if (seenSites.has(site.uid)) continue;
    seenSites.add(site.uid);

    for (const availability of site.infrastructure_availabilities ?? []) {
      const key = `${site.uid}${KEY_SEPARATOR}${availability.infrastructure_uid}`;
      const total = totals.get(key);
      if (total) total.quantity += availability.quantity;
      else
        totals.set(key, {
          siteUid: site.uid,
          infrastructureUid: availability.infrastructure_uid,
          quantity: availability.quantity
        });
    }
  }

  return [...totals.values()];
}

/**
 * Indexes the display names of infrastructure rows. Rows without a name are skipped so
 * the series keeps its uid fallback rather than showing an empty label.
 */
export function collectInfrastructureNames(
  infrastructures: readonly { uid: string; name?: string }[]
): Map<string, string> {
  const names = new Map<string, string>();
  for (const infrastructure of infrastructures) {
    if (infrastructure.name) names.set(infrastructure.uid, infrastructure.name);
  }
  return names;
}

/**
 * Builds one series per (site, infrastructure) pair.
 *
 * The pair set is the union of what sites declare and what routes claim, so an idle
 * dock bank still appears (capacity with no usage) and an undeclared claim still
 * appears (usage with null capacity).
 */
export function buildSeriesFromContributions(
  contributions: readonly OccupationContribution[],
  siteUids?: ReadonlySet<string>
): OccupationSeries[] {
  const claims: OccupationClaim[] = [];
  const capacities = new Map<string, number>();
  const infrastructureNames = new Map<string, string>();

  for (const contribution of contributions) {
    for (const claim of contribution.claims) {
      if (siteUids && !siteUids.has(claim.siteUid)) continue;
      claims.push(claim);
    }

    for (const availability of contribution.availabilities) {
      if (siteUids && !siteUids.has(availability.siteUid)) continue;
      capacities.set(
        `${availability.siteUid}${KEY_SEPARATOR}${availability.infrastructureUid}`,
        availability.quantity
      );
    }

    for (const [uid, name] of contribution.infrastructureNames) {
      infrastructureNames.set(uid, name);
    }
  }

  // A set holding no claims at all cannot tell an idle plan from one whose usages were never
  // derived, so the verdict is drawn here — where the whole set is in view — rather than per
  // series, where a legitimately unused infrastructure looks identical.
  const usagesReported = claims.length > 0;

  const claimsByPair = new Map<string, OccupationClaim[]>();
  for (const c of claims) {
    const key = `${c.siteUid}${KEY_SEPARATOR}${c.infrastructureUid}`;
    const bucket = claimsByPair.get(key);
    if (bucket) bucket.push(c);
    else claimsByPair.set(key, [c]);
  }

  const pairs = [...new Set([...capacities.keys(), ...claimsByPair.keys()])].sort(
    (a, b) => {
      const [aSite, aInfra] = a.split(KEY_SEPARATOR);
      const [bSite, bInfra] = b.split(KEY_SEPARATOR);
      return aSite.localeCompare(bSite) || aInfra.localeCompare(bInfra);
    }
  );

  return pairs.map((key) => {
    const [siteUid, infrastructureUid] = key.split(KEY_SEPARATOR);
    const pairClaims = claimsByPair.get(key) ?? [];
    const capacity = capacities.has(key) ? (capacities.get(key) as number) : null;
    const steps = sweep(pairClaims);

    return {
      siteUid,
      infrastructureUid,
      infrastructureName: infrastructureNames.get(infrastructureUid) ?? infrastructureUid,
      capacity,
      usagesReported,
      steps,
      // A lane models one whole unit, so only a ladder-eligible capacity can use them.
      // Packing them regardless would allocate one lane per unit of quantity — hundreds
      // of them for an area or weight capacity, at a quadratic cost in the first fit.
      lanes: canLadder(capacity) ? packLanes(pairClaims) : [],
      ...summarize(steps, capacity)
    };
  });
}

/**
 * Builds the series for a set of sites from the stopovers made at them.
 *
 * Stopovers rather than routes: a stopover carries the usages and names its own route, and
 * the fetch that feeds this asks for every stopover at a site regardless of which routes a
 * table happens to hold.
 */
export function buildOccupationSeries(
  stopovers: readonly UserRouteActionV1Model[],
  sites: readonly UserSiteV1Model[],
  infrastructureNames: ReadonlyMap<string, string>,
  siteUids?: ReadonlySet<string>
): OccupationSeries[] {
  return buildSeriesFromContributions(
    [
      {
        claims: stopovers.flatMap((stopover) =>
          collectActionClaims(stopover, stopover.route_uid)
        ),
        availabilities: collectAvailabilities(sites),
        infrastructureNames
      }
    ],
    siteUids
  );
}
